import { Component } from 'vue';
import { MCampaignWidgetOptionsProps, MCampaignWidgetProps } from '@/exports/MCampaignWidgetProps';
import MCampaignWrapper from '@/exports/MCampaignWrapper.vue';
// Props
import { MCartWidgetOptionsProps, MCartWidgetProps } from '@/exports/MCartWidgetProps';
// Wrappers
import MCartWrapper from '@/exports/MCartWrapper.vue';
import { MCheckoutWidgetOptionsProps, MCheckoutWidgetProps } from '@/exports/MCheckoutWidgetProps';
import MCheckoutWrapper from '@/exports/MCheckoutWrapper.vue';
// Widgets
import MAddToCart from '@/exports/widget/MAddToCart.vue';
import MCampaign from '@/exports/widget/MCampaign.vue';
import MCheckout from '@/exports/widget/MCheckout.vue';
import MCountdown from '@/exports/widget/MCountdown.vue';
import MTokenCart from '@/exports/widget/MTokenCart.vue';
import MTokenCartItemDelete from '@/exports/widget/MTokenCartItemDelete.vue';
import MTokenChecker from '@/exports/widget/MTokenChecker.vue';

// Base Campaign Widget "m-campaign"
const BaseCampaignWidget = {
  props: MCampaignWidgetProps,
  wrapper: MCampaignWrapper,
  defaults: MCampaignWidgetOptionsProps
};

// Shopify Widget for "m-add-to-cart"
const AddToCartWidget = {
  props: MCartWidgetProps,
  wrapper: MCartWrapper,
  defaults: MCartWidgetOptionsProps
};

// Shopify Widget for "m-checkout"
const CheckoutWidget = {
  props: MCheckoutWidgetProps,
  wrapper: MCheckoutWrapper,
  defaults: MCheckoutWidgetOptionsProps
};

// Shopify Widget for "m-token-cart"
const TokenCartWidget = {
  props: MCartWidgetProps,
  wrapper: MCartWrapper,
  defaults: MCartWidgetOptionsProps
};

// Shopify Widget for "m-token-cart-item-delete"
const TokenCartItemDeleteWidget = {
  props: MCheckoutWidgetProps,
  wrapper: MCheckoutWrapper,
  defaults: MCheckoutWidgetOptionsProps
};

// Shopify Widget for "m-countdown"
const CountdownWidget = {
  props: MCampaignWidgetProps,
  wrapper: MCampaignWrapper,
  defaults: MCampaignWidgetOptionsProps
};

// Shopify Widget for "m-token-checker"
const TokenCheckerWidget = {
  props: MCampaignWidgetProps,
  wrapper: MCampaignWrapper,
  defaults: MCampaignWidgetOptionsProps
};

interface WidgetMetadata {
  component: Component;
  props: Component; // props to pass to the wrapper component
  wrapper: Component; // provider wrapper component
  //@ts-ignore
  defaults: Record<string, unknown>; //Object
}

/**
 * Mapping of widget names accessible through the `data-widget` prop to their
 * implementation details.
 * Each entry should be of the form:
 * <widget-name>: {
 *    component: <Vue component>,
 *    props: <Vue props>, // props to pass to the wrapper component
 *    wrapper: <Vue component>, // provider wrapper component
 *    defaults: object details the `props` data passed to the @Options decorator,
 * }
 */
const Widgets = {
  'm-campaign': {
    component: MCampaign,
    ...BaseCampaignWidget
  },
  'm-add-to-cart': {
    component: MAddToCart,
    ...AddToCartWidget
  },
  'm-checkout': {
    component: MCheckout,
    ...CheckoutWidget
  },
  'm-token-cart': {
    component: MTokenCart,
    ...TokenCartWidget
  },
  'm-token-cart-item-delete': {
    component: MTokenCartItemDelete,
    ...TokenCartItemDeleteWidget
  },
  'm-token-checker': {
    component: MTokenChecker,
    ...TokenCheckerWidget
  },
  'm-countdown': {
    component: MCountdown,
    ...CountdownWidget
  }

  // @dev: add any additional exports here
} as const; // necessary to extract types from this

type DataWidgetName = keyof typeof Widgets;

const BaseWidgets: { [widgetName: string]: WidgetMetadata } = {};

Object.entries(Widgets).forEach(([name, widget]) => {
  BaseWidgets[name as string] = widget;
});

function isRecognisedWidgetName(name?: string): name is DataWidgetName {
  const stringName = `${name}`;
  if (!stringName) {
    return false;
  }

  return !!Widgets[stringName as DataWidgetName];
}

export { Widgets, BaseWidgets, DataWidgetName, isRecognisedWidgetName };
